import { InvoiceStatus } from '@constants'
import { useTheme } from '@hooks'
import { Badge, TextTooltip } from '@root/components'
import { IInvoice } from '@query'
import { formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ReminderMark } from '@pages/InvoicingPage/postDueUi.ts'
import { BankMonitoringHint } from '@pages/InvoicingPage/DebtCollection/trackerMessages/BankMonitoringHint'
import { useBankMonitoringStatus } from '@pages/InvoicingPage/useBankMonitoringStatus'
import {
  InvoiceDebtCollectionIcon,
  InvoiceNotSentIcon,
  InvoiceReadyToSendIcon,
  InvoiceSentIcon,
  InvoiceSoftReminderIcon,
  InvoiceWatchOnIcon,
  invoiceStatusIcons,
  PurchaseInvoicePaidIcon,
  PurchaseInvoiceUnpaidIcon
} from '@pages/InvoicingPage/invoiceIcons'
import { FaQuestionCircle } from 'react-icons/fa'
import styled from 'styled-components'

interface StatusPillProps {
  label: React.ReactNode
  color: string
  Icon?: React.ElementType
  trailing?: React.ReactNode
}

/** Files-page pill chrome (white + gray border) with the previous colored status icons. */
const StatusPill: React.FC<StatusPillProps> = ({ label, color, Icon, trailing }) => (
  <Badge type="default">
    <PillContent $color={color}>
      {Icon && <Icon />}
      <span>{label}</span>
      {trailing}
    </PillContent>
  </Badge>
)

interface Props {
  status: InvoiceStatus
  iconOnly?: boolean
  hideIcon?: boolean
  size?: 'md' | 'sm'
}

export const InvoiceStatusBadge: React.FC<Props> = ({
  status,
  hideIcon = false,
  iconOnly = false,
  size = 'md'
}) => {
  const theme = useTheme()
  const [t] = useTranslation()

  interface InvoiceStatusOption {
    text: string
    Icon: React.ElementType
    color: string
  }

  const statusOptions: Record<InvoiceStatus, InvoiceStatusOption> = {
    [InvoiceStatus.DRAFT]: {
      text: t('invoicing.status.draft'),
      Icon: invoiceStatusIcons[InvoiceStatus.DRAFT],
      color: theme.colors.warning
    },
    [InvoiceStatus.ACCEPTED]: {
      text: t('invoicing.status.accepted'),
      Icon: invoiceStatusIcons[InvoiceStatus.ACCEPTED],
      color: theme.colors.primary
    },
    [InvoiceStatus.CREDIT_LOSS]: {
      text: t('invoicing.status.creditLoss'),
      Icon: invoiceStatusIcons[InvoiceStatus.CREDIT_LOSS],
      color: theme.colors.dashboardNeg
    },
    [InvoiceStatus.PAID]: {
      text: t('invoicing.status.paid'),
      Icon: invoiceStatusIcons[InvoiceStatus.PAID],
      color: theme.colors.success
    }
  }

  const statusOption = statusOptions[status]
  if (!statusOption) return null

  if (iconOnly) {
    return (
      <StyledIconWrapper $color={statusOption.color}>
        <statusOption.Icon color={statusOption.color} />
      </StyledIconWrapper>
    )
  }

  if (size === 'sm') {
    return (
      <LegacyTintedChip className="sm" $color={statusOption.color}>
        {!hideIcon && <statusOption.Icon />}
        <span>{statusOption.text}</span>
      </LegacyTintedChip>
    )
  }

  return (
    <StatusPill
      label={statusOption.text}
      color={statusOption.color}
      Icon={hideIcon ? undefined : statusOption.Icon}
      trailing={
        status === InvoiceStatus.DRAFT ? (
          <TextTooltip tooltip={t('invoicing.details.numberNotGenerated')}>
            <FaQuestionCircle />
          </TextTooltip>
        ) : undefined
      }
    />
  )
}

/** Header chip shown while an invoice is confirmed but not yet delivered. */
export const InvoiceReadyToSendChip: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <StatusPill
      label={t('invoicing.status.readyToSend')}
      color={theme.colors.primary}
      Icon={InvoiceReadyToSendIcon}
    />
  )
}

export const InvoiceSentBadge: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <StatusPill
      label={t('invoicing.status.sent')}
      color={theme.colors.primary}
      Icon={InvoiceSentIcon}
    />
  )
}

export const InvoiceNotSentBadge: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <StatusPill
      label={t('invoicing.status.notSent')}
      color={theme.colors.warning}
      Icon={InvoiceNotSentIcon}
      trailing={
        <TextTooltip
          tooltip={t('invoicing.status.notSentTip', {
            sendAction: t('invoicing.details.send')
          })}
        >
          <FaQuestionCircle />
        </TextTooltip>
      }
    />
  )
}

const KraviaBadge: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <StatusPill
      label={t('invoicing.status.debtCollection')}
      color={theme.colors.kraviaOrange}
      Icon={InvoiceDebtCollectionIcon}
      trailing={
        <TextTooltip tooltip={t('invoicing.status.debtCollectionTip')}>
          <FaQuestionCircle />
        </TextTooltip>
      }
    />
  )
}

const SoftReminderSentBadge: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <StatusPill
      label={t('invoicing.status.reminder')}
      color={theme.colors.warning}
      Icon={InvoiceSoftReminderIcon}
      trailing={
        <TextTooltip tooltip={t('invoicing.status.reminderTip')}>
          <FaQuestionCircle />
        </TextTooltip>
      }
    />
  )
}

const SoftReminderFailedBadge: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <StatusPill
      label={t('invoicing.status.reminderFailed')}
      color={theme.colors.dashboardNeg}
      Icon={InvoiceSoftReminderIcon}
      trailing={
        <TextTooltip tooltip={t('invoicing.status.reminderFailedTip')}>
          <FaQuestionCircle />
        </TextTooltip>
      }
    />
  )
}

export const InvoiceWatchOnBadge: React.FC<{ invoice: IInvoice }> = ({ invoice }) => {
  const theme = useTheme()
  const [t] = useTranslation()
  const { variant: bankMonitoringVariant } = useBankMonitoringStatus()
  const schedule = invoice.post_due?.schedule ?? []
  const tooltip = (
    <WatchTooltip>
      {schedule
        .filter(item => item.kind === 'reminder' || item.kind === 'collection')
        .map(item => {
          const ordinal = Math.min(Math.max(item.reminder_number ?? 1, 1), 5)
          const label =
            item.kind === 'collection'
              ? t('invoicing.debtCollection.reminders.collectionScheduled')
              : t('invoicing.debtCollection.reminders.watchReminderLine', {
                  number: ordinal,
                  date: formatDate(item.at)
                })
          return (
            <div key={`${item.kind}-${item.at}`}>
              {item.kind === 'collection' ? `${label}: ${formatDate(item.at)}` : label}
            </div>
          )
        })}
    </WatchTooltip>
  )

  return (
    <StatusPill
      label={t('invoicing.status.watchOn')}
      color={theme.colors.primary}
      Icon={InvoiceWatchOnIcon}
      trailing={
        <>
          {bankMonitoringVariant && <BankMonitoringHint variant={bankMonitoringVariant} />}
          <TextTooltip tooltip={tooltip} maxWidth={320}>
            <FaQuestionCircle />
          </TextTooltip>
        </>
      }
    />
  )
}

const WatchTooltip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

interface PostDueStatusBadgeProps {
  reminder: ReminderMark | null
  kravia: boolean
}

export const PostDueStatusBadges: React.FC<PostDueStatusBadgeProps> = ({ reminder, kravia }) => (
  <>
    {reminder === 'sent' && <SoftReminderSentBadge />}
    {reminder === 'failed' && <SoftReminderFailedBadge />}
    {kravia && <KraviaBadge />}
  </>
)

export const PurchaseInvoiceStatusBadge: React.FC<{
  isPaid: boolean
  size: string
  hideIcon?: boolean
  iconOnly?: boolean
}> = ({ size, isPaid, iconOnly = false, hideIcon = false }) => {
  const theme = useTheme()
  const [t] = useTranslation()
  const color = isPaid ? theme.colors.success : theme.colors.warning
  const Icon = isPaid ? PurchaseInvoicePaidIcon : PurchaseInvoiceUnpaidIcon
  const text = isPaid ? t('invoicing.status.paid') : t('invoicing.status.unpaid')

  if (iconOnly) return <Icon color={color} />

  if (size === 'sm') {
    return (
      <LegacyTintedChip className="sm" $color={color}>
        {!hideIcon && <Icon />}
        <span>{text}</span>
      </LegacyTintedChip>
    )
  }

  return <StatusPill label={text} color={color} Icon={hideIcon ? undefined : Icon} />
}

export const InvoiceRecurrenceDisabledBadge: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <StatusPill
      label={t('invoicing.details.recurrence.badge.text')}
      color={theme.colors.iconGray}
      trailing={
        <TextTooltip tooltip={t('invoicing.details.recurrence.badge.tip')}>
          <FaQuestionCircle size={14} />
        </TextTooltip>
      }
    />
  )
}

export const DebtCollectionIcon: React.FC = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <TextTooltip tooltip={t('invoicing.status.debtCollectionTip')}>
      <StyledIconWrapper>
        <InvoiceDebtCollectionIcon color={theme.colors.kraviaOrange} />
      </StyledIconWrapper>
    </TextTooltip>
  )
}

export const SoftReminderListIcon: React.FC<{ failed?: boolean }> = ({ failed = false }) => {
  const theme = useTheme()
  const [t] = useTranslation()

  return (
    <TextTooltip
      tooltip={failed ? t('invoicing.status.reminderFailedTip') : t('invoicing.status.reminderTip')}
    >
      <StyledIconWrapper>
        <InvoiceSoftReminderIcon
          color={failed ? theme.colors.dashboardNeg : theme.colors.warning}
        />
      </StyledIconWrapper>
    </TextTooltip>
  )
}

const PillContent = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  max-width: 18rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  > svg:first-of-type {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
    flex-shrink: 0;
    color: ${({ $color }) => $color};
    fill: ${({ $color }) => $color};
  }

  /* Tooltip / trailing icons stay muted with the label */
  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
    flex-shrink: 0;
  }
`

/** Kept for list-row compact chips (size=sm / iconOnly). */
const LegacyTintedChip = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}22;
  border-radius: 0.4rem;
  font-weight: 500;
  display: flex;
  align-items: center;

  &.sm {
    padding: 0.2rem 0.3rem;
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
    gap: ${({ theme }) => theme.spacing.xxs}rem;
  }
`

const StyledIconWrapper = styled.div<{ $color?: string; $pulse?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.2rem;
  height: 1.2rem;
  background: ${({ $color }) => ($color ? `${$color}22` : 'none')};
  border-radius: 6px;

  svg {
    width: 0.8rem;
    height: 0.8rem;
  }

  ${({ $pulse }) =>
    $pulse
      ? `
    animation: pulse 3s linear infinite;
  `
      : null};

  @keyframes pulse {
    0% {
      opacity: 0.8;
    }
    50% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.8;
    }
  }
`
